import { ComponentGroup, HeatingSystem } from '@/util/modbus';
import { metricNameForMeasurementTypes } from './shared';

export type WarmwasseraufbereitungLabel = 'W' | 'Z' | 'VL' | 'RL';
export type WarmwasseraufbereitungLabelPos = 'firstLabel' | 'secondLabel' | 'thirdLabel' | 'fourthLabel';

const DEFAULT_WARMWASSERAUFBEREITUNG_LABELS: WarmwasseraufbereitungLabel[] = ['W', 'Z'];
const ALL_WARMWASSERAUFBEREITUNG_LABELS: WarmwasseraufbereitungLabel[] = ['W', 'Z', 'VL', 'RL'];

export function isWarmwasseraufbereitung(groupName: string): boolean {
  return groupName.toLowerCase().includes('warmwasseraufbereitung');
}

export function warmwasseraufbereitungShortLabelFromMeasurementType(
  measurementType?: string | null,
): WarmwasseraufbereitungLabel | null {
  if (!measurementType) return null;
  const normalized = measurementType.toLowerCase();

  // Match specific primary flow/return first; measurementType can include "warmwasser"
  // as part of the component name and would otherwise shadow VL/RL detection.
  if (normalized.includes('vorlauf')) return 'VL';
  if (normalized.includes('rücklauf')) return 'RL';
  if (normalized.includes('zirkulation')) return 'Z';
  if (normalized.includes('warmwasser')) return 'W';

  return null;
}

export function labelPosForWarmwasseraufbereitungLabel(
  label: WarmwasseraufbereitungLabel,
): WarmwasseraufbereitungLabelPos {
  switch (label) {
    case 'W':
      return 'firstLabel';
    case 'Z':
      return 'secondLabel';
    case 'VL':
      return 'thirdLabel';
    case 'RL':
      return 'fourthLabel';
    default:
      return 'firstLabel';
  }
}

export function getNeededWarmwasseraufbereitungGroupNames(components: ComponentGroup[]): Set<string> {
  return new Set(components.filter((c) => isWarmwasseraufbereitung(c.groupName)).map((c) => c.groupName));
}

export function initPresentWarmwasserByGroupName(
  groupNames: Set<string>,
): Record<string, Set<WarmwasseraufbereitungLabel>> {
  const presentByGroupName: Record<string, Set<WarmwasseraufbereitungLabel>> = {};
  for (const groupName of groupNames) {
    presentByGroupName[groupName] = new Set<WarmwasseraufbereitungLabel>();
  }
  return presentByGroupName;
}

export function addPresentWarmwasseraufbereitungLabels(
  presentByGroupName: Record<string, Set<WarmwasseraufbereitungLabel>>,
  neededGroupNames: Set<string>,
  heatingSystems: HeatingSystem[],
): void {
  for (const hs of heatingSystems) {
    for (const group of hs.heatingSystemMeasurementGroups) {
      if (!neededGroupNames.has(group.groupName)) continue;

      const present = presentByGroupName[group.groupName];
      for (const measurement of group.heatingSystemMeasurements) {
        const label = warmwasseraufbereitungShortLabelFromMeasurementType(measurement.measurementType);
        if (label) {
          present.add(label);
        }
      }
    }
  }
}
// This function resolves the labels for a Warmwasseraufbereitung group, ensuring a consistent order and handling incomplete/mixed cases.
export function resolveOrderedWarmwasseraufbereitungLabels(
  present: Set<WarmwasseraufbereitungLabel>,
): WarmwasseraufbereitungLabel[] {
  const hasAllFour = ALL_WARMWASSERAUFBEREITUNG_LABELS.every((label) => present.has(label));
  if (hasAllFour) return ALL_WARMWASSERAUFBEREITUNG_LABELS;

  if (present.has('W') && present.has('Z')) return ['W', 'Z'];
  if (present.has('VL') && present.has('RL')) return ['VL', 'RL'];

  const picked = ALL_WARMWASSERAUFBEREITUNG_LABELS.filter((l) => present.has(l)).slice(0, 2);
  return picked.length === 2 ? picked : DEFAULT_WARMWASSERAUFBEREITUNG_LABELS;
}

export function metricNameForWarmwasseraufbereitungLabel(
  measurements: Array<{ measurementType?: string | null; metricName?: string | null }> | null | undefined,
  label: WarmwasseraufbereitungLabel,
): string | null {
  if (!measurements || !Array.isArray(measurements)) return null;

  const byLabel: Record<WarmwasseraufbereitungLabel, string[]> = {
    W: ['warmwasser'],
    Z: ['zirkulation'],
    VL: ['vorlauf'],
    RL: ['rücklauf'],
  };

  return metricNameForMeasurementTypes(measurements, byLabel[label]);
}

export function findWarmwasseraufbereitungGroupByLabel(
  groups: HeatingSystem['heatingSystemMeasurementGroups'],
  label: WarmwasseraufbereitungLabel,
): HeatingSystem['heatingSystemMeasurementGroups'][number] | null {
  return (
    groups.find((group) =>
      group.heatingSystemMeasurements?.some(
        (m) => warmwasseraufbereitungShortLabelFromMeasurementType(m.measurementType) === label,
      ),
    ) ?? null
  );
}
